/**
 * 確認の画面の「見える所」の日本語（en.confirmPlain.ts と同じキーを全部持つ。漏れは typecheck で落ちる）。
 * 中の言葉（本番・環境・バージョン・エージェント・束…）は書かない（confirmPlain.ts PLAIN_FORBIDDEN・confirmPlain.test.ts）。
 * 言い方: 見出しは「〜しますか？」・結果は目に見えることを 1 文・「AI」「遊べる」「みんな」など利用者の言葉。
 * 2 回目（2026-10-04 console/plain-confirm-2・founder「日本語が回りくどいというか自然じゃない」）: ふつうの日本のアプリが言う短い言い方に。
 *   ダメと言われた例 =「サーバーのプログラムを新しくしますか？」「使っている人にすぐ届きます」「何を実行するかは『くわしく見る』で確かめて」。
 *   「本番」「環境」「プログラム」「BAN」「異議」「秘密の値」「前払い残高」は見える所に出さない（PLAIN_FORBIDDEN）。
 *   データの書き換え（db_query）は SQL を読んで具体的に言う（sqlEffect.ts）= 判断に要ることを「詳細」に投げない。
 */
import type { enConfirmPlain } from './en.confirmPlain';

type ConfirmPlainMessages = { [K in keyof typeof enConfirmPlain]: string };

export const jaConfirmPlain: ConfirmPlainMessages = {
  'confirmPlain.pageTitle': '確認してください',
  'confirmPlain.loading': '読み込んでいます',
  'confirmPlain.cancel': 'やめる',
  'confirmPlain.safety': '心当たりがなければ「やめる」を押してください。何も変わりません。',
  'confirmPlain.safetyCli': '心当たりがなければ「やめる」を押してください。何も変わりません。',
  'confirmPlain.details': '詳細',
  'confirmPlain.stopsGame': 'もう一度公開するまで、誰も開けなくなります。',
  'confirmPlain.alsoOn': '{list}も使えるようになります。',
  // 公開と同じ 1 回でいっしょにすること（2026-10-08 core/one-confirm・confirmPlain.ts PlainSummary.alsoDo）。名詞の形で並べる（「サーバーの開始とサーバーの更新」）
  'confirmPlain.alsoDo': 'あわせて、{list}もします。',
  'confirmPlain.alsoDo.serverOn': 'サーバーの開始',
  'confirmPlain.alsoDo.serverUpdate': 'サーバーの更新',
  'confirmPlain.alsoDo.settings': '設定の変更',
  'confirmPlain.alsoDo.dataShape': 'データの保存形式の変更',
  'confirmPlain.alsoDo.domain': '独自のアドレスの追加',
  // 公開の費用の目安（2026-10-04 money/publish-cost・confirmPlain.ts costOf）。結果の行の下に小さく。
  // 「遊ばれて」でなく「開かれると」: ダッシュボードの言葉は「アプリ / 利用者」（ゲームに限らない・i18n/wording.test.ts）+ 結果の行の「誰でも開けるようになります」と揃える。「ほぼ」は使わない（founder）
  'confirmPlain.cost': '費用の目安: 保存は月 {storage}・1 万回開かれると {per10k}',
  'confirmPlain.otherChanges': 'ほかの変更もあります。',
  // 文と文の間（en は空白・ja は無し）
  'confirmPlain.sentenceGap': '',
  // 並べ方: 「セーブ、スコアとマルチプレイ」（最後の 2 つの間だけ listLast）
  'confirmPlain.listJoin': '、',
  'confirmPlain.listLast': 'と',
  // 表の名前を並べる（「scores」「users」）。前後の「」は文の側
  'confirmPlain.tableJoin': '」「',
  'confirmPlain.feature.saves': 'セーブ',
  // stats は指示書では「ランキング」だったが、ランキングは最初の公開で閉じている機能の名前（launchHidden・wordsPass.test.ts が止める）= 「スコア」
  'confirmPlain.feature.stats': 'スコア',
  'confirmPlain.feature.multiplayer': 'マルチプレイ',
  'confirmPlain.feature.emailLogin': 'メールでのログイン',
  'confirmPlain.feature.googleLogin': 'Google でのログイン',
  'confirmPlain.feature.discordLogin': 'Discord でのログイン',
  'confirmPlain.feature.appleLogin': 'Apple でのログイン',
  'confirmPlain.feature.xLogin': 'X でのログイン',
  'confirmPlain.feature.realtimeChannels': 'リアルタイム通信',
  'confirmPlain.feature.voice': '音声通話',
  'confirmPlain.feature.p2p': '端末どうしの直接通信',
  'confirmPlain.feature.other': 'ほかの必要な機能',

  'confirmPlain.headline.publish': '「{name}」を公開しますか？',
  'confirmPlain.headline.publish_noName': 'アプリを公開しますか？',
  'confirmPlain.headline.publishTest': '「{name}」をテスト用に公開しますか？',
  'confirmPlain.headline.publishTest_noName': 'アプリをテスト用に公開しますか？',
  'confirmPlain.headline.allowPublish': '「{name}」を公開できる状態にしますか？',
  'confirmPlain.headline.allowPublish_noName': 'アプリを公開できる状態にしますか？',
  'confirmPlain.headline.stop': '「{name}」の公開をやめますか？',
  'confirmPlain.headline.stop_noName': 'アプリの公開をやめますか？',
  'confirmPlain.headline.turnOn': '「{name}」に新しい機能を追加しますか？',
  'confirmPlain.headline.turnOn_noName': 'アプリに新しい機能を追加しますか？',
  'confirmPlain.headline.settings': '「{name}」の設定を変更しますか？',
  'confirmPlain.headline.settings_noName': 'アプリの設定を変更しますか？',
  // 独自ドメインは配信（hosting）もサーバーも同じ言い方（利用者から見ると「そのアドレスで開ける」だけ）
  'confirmPlain.headline.domainAdd': '「{name}」を {hostname} でも開けるようにしますか？',
  'confirmPlain.headline.domainAdd_noName': 'アプリを {hostname} でも開けるようにしますか？',
  'confirmPlain.headline.domainRemove': '「{name}」を {hostname} で開けないようにしますか？',
  'confirmPlain.headline.domainRemove_noName': 'アプリを {hostname} で開けないようにしますか？',
  'confirmPlain.headline.serverOn': '「{name}」のサーバーを動かしますか？',
  'confirmPlain.headline.serverOn_noName': 'アプリのサーバーを動かしますか？',
  'confirmPlain.headline.serverOff': '「{name}」のサーバーを止めますか？',
  'confirmPlain.headline.serverOff_noName': 'アプリのサーバーを止めますか？',
  'confirmPlain.headline.serverDelete': '「{name}」のサーバーを削除しますか？',
  'confirmPlain.headline.serverDelete_noName': 'アプリのサーバーを削除しますか？',
  'confirmPlain.headline.serverPurge': '「{name}」のサーバーを、保存したデータごと削除しますか？',
  'confirmPlain.headline.serverPurge_noName': 'アプリのサーバーを、保存したデータごと削除しますか？',
  'confirmPlain.headline.serverUpdate': '「{name}」のサーバーを更新しますか？',
  'confirmPlain.headline.serverUpdate_noName': 'アプリのサーバーを更新しますか？',
  'confirmPlain.headline.dataChange': '「{name}」のデータを書き換えますか？',
  'confirmPlain.headline.dataChange_noName': 'アプリのデータを書き換えますか？',
  // SQL が読むだけ（sqlEffect = read）のとき。「書き換えますか？」と聞くと結果の行（何も変わりません）と食い違う
  'confirmPlain.headline.dataRead': '「{name}」のデータを読みますか？',
  'confirmPlain.headline.dataRead_noName': 'アプリのデータを読みますか？',
  // SQL の種類で見出しを変える（2026-10-04 管理: 結果の行と食い違わないように）
  'confirmPlain.headline.dataDelete': '「{name}」のデータを消しますか？',
  'confirmPlain.headline.dataDelete_noName': 'アプリのデータを消しますか？',
  'confirmPlain.headline.dataAdd': '「{name}」にデータを追加しますか？',
  'confirmPlain.headline.dataAdd_noName': 'アプリにデータを追加しますか？',
  'confirmPlain.headline.dataShape': '「{name}」のデータの保存形式を変更しますか？',
  'confirmPlain.headline.dataShape_noName': 'アプリのデータの保存形式を変更しますか？',
  'confirmPlain.headline.other': '「{name}」を変更しますか？',
  'confirmPlain.headline.other_noName': 'アプリを変更しますか？',
  'confirmPlain.headline.ban': '{playerId} さんを利用停止にしますか？',
  'confirmPlain.headline.unban': '{playerId} さんの利用停止を解除しますか？',
  'confirmPlain.headline.appealAccept': '利用停止の解除のお願いを受け入れますか？',
  'confirmPlain.headline.appealReject': '利用停止の解除のお願いを断りますか？',
  'confirmPlain.headline.secretPut': '{secret} を保存しますか？',
  'confirmPlain.headline.secretDelete': '{secret} を削除しますか？',
  'confirmPlain.headline.mailSchedule': 'メール「{subject}」の送信を予約しますか？',
  'confirmPlain.headline.mailRepeat': 'メール「{subject}」を定期的に送りますか？',
  'confirmPlain.headline.mailSend': 'メール「{subject}」を今すぐ送りますか？',

  'confirmPlain.result.publish': '{url} で、誰でも開けるようになります。',
  'confirmPlain.result.publish_noUrl': '誰でも開けるようになります。',
  'confirmPlain.result.publishTest': '{url} で試せるようになります。',
  'confirmPlain.result.publishTest_noUrl': 'テスト用のアプリで試せるようになります。',
  // 押した後・もう一度開いたとき（2026-10-08 publish-url）。{url} は新しいタブで開くリンク
  // 公開されたかの 1 行（ページの一番上・2026-10-13 console/confirm-wait）
  'confirmPlain.state.notYet': 'まだ公開されていません。下のボタンを押すと公開されます。',
  'confirmPlain.state.notYetTest': 'まだテスト用のアプリに公開されていません。下のボタンを押すと公開されます。',
  'confirmPlain.state.approvedChat': '公開の準備中です。AI が続けると公開されます。',
  'confirmPlain.state.approvedCli': '公開の準備中です。ふつうは数秒で終わります。',
  'confirmPlain.state.published': '公開されました。',
  'confirmPlain.state.publishedTest': 'テスト用のアプリに公開されました。',
  'confirmPlain.state.expired': '公開されていません。このリンクは期限が切れました。',
  'confirmPlain.state.cancelled': '公開されていません。取り消しました。',
  'confirmPlain.state.notPublished': '公開されていません。',
  'confirmPlain.done.publish': '許可しました。まもなく {url} で公開されます。',
  'confirmPlain.done.publishTest': '許可しました。まもなくテスト用の {url} で試せます。',
  'confirmPlain.done.published': '公開済みです。{url} で開けます。',
  'confirmPlain.done.publishedTest': 'テスト用に公開済みです。{url} で試せます。',
  // 押した後の見せ方（2026-10-15 console/confirm-publishing・en.confirmPlain.ts と同じ並び・confirmPublishing.ts の WHY）
  'confirmPlain.publishing.title': '公開の準備中です',
  'confirmPlain.publishing.titleTest': 'テスト用のアプリに公開する準備中です',
  'confirmPlain.publishing.waitCli': 'あなたのパソコンの {cliName} が公開の手続きを進めています。ふつうは数秒で終わります。',
  'confirmPlain.publishing.waitCliSlow': 'あなたのパソコンの {cliName} が、まだ公開の手続きを進めています。',
  'confirmPlain.publishing.waitChat': 'あなたの AI が続きを実行すると公開されます。',
  'confirmPlain.publishing.where': '公開先: {url}',
  'confirmPlain.publishing.leaveOpen': 'この画面を開いたままにすると、終わったときに表示が変わります。閉じてもかまいません。',
  'confirmPlain.publishing.slowCli': 'いつもより時間がかかっています。{cliName} を動かしている画面にエラーが出ていないか確かめてください。',
  'confirmPlain.publishing.unknownTitle': 'まだ終わったという知らせがありません',
  'confirmPlain.publishing.unknown': '{minutes} 分たったので、この画面での確認を止めました。読み込み直すと、いまの状態が分かります。',
  'confirmPlain.publishing.reload': '読み込み直す',
  'confirmPlain.published.title': '公開しました',
  'confirmPlain.published.titleTest': 'テスト用のアプリに公開しました',
  'confirmPlain.published.at': '{url} で誰でも開けます。',
  'confirmPlain.published.atTest': '{url} で試せます。',
  'confirmPlain.published.noUrl': 'いまから誰でも開けます。',
  'confirmPlain.published.noUrlTest': 'テスト用のアプリで試せます。',
  'confirmPlain.published.open': 'アプリを開く',
  'confirmPlain.published.openTest': 'テスト用のアプリを開く',
  'confirmPlain.stopped.title': '公開されませんでした',
  'confirmPlain.stopped.cancelledTitle': '取り消しました',
  'confirmPlain.stopped.expiredCli': '時間内に終わらなかったため、何も変わっていません。公開するには、パソコンで同じコマンドをもう一度実行してください。新しい確認のページが開きます。',
  'confirmPlain.stopped.staleCli': 'このページを作った後に中身が変わったため、何も変わっていません。公開するには、パソコンで同じコマンドをもう一度実行してください。新しい確認のページが開きます。',
  // CLI がもう待っていない（2026-10-15 core/confirm-done・en.confirmPlain.ts と同じ並び）
  'confirmPlain.notWaiting.title': 'ここで押しても、もう何も起きません',
  'confirmPlain.notWaiting.linePublish': 'あなたのパソコンの {cliName} が、このページを待つのをやめました（止められたか、画面が閉じられました）。このページからはもう公開できません。何も変わっていません。',
  'confirmPlain.notWaiting.line': 'あなたのパソコンの {cliName} が、このページを待つのをやめました（止められたか、画面が閉じられました）。このページからはもう変更できません。何も変わっていません。',
  'confirmPlain.notWaiting.nextPublish': '公開するには、パソコンで同じコマンドをもう一度実行し、新しく開いたページでボタンを押してください。',
  'confirmPlain.notWaiting.next': 'パソコンで同じコマンドをもう一度実行し、新しく開いたページでボタンを押してください。',
  'confirmPlain.stopped.notWaitingCli': 'あなたのパソコンの {cliName} が途中で止まったため、何も変わっていません。公開するには、パソコンで同じコマンドをもう一度実行してください。新しい確認のページが開きます。',
  'confirmPlain.result.allowPublish': 'このあと、{url} で公開できるようになります。',
  'confirmPlain.result.allowPublish_noUrl': 'このあと、公開できるようになります。',
  'confirmPlain.result.stop': '{url} を開いても、何も表示されなくなります。',
  'confirmPlain.result.stop_noUrl': 'アプリのリンクを開いても、何も表示されなくなります。',
  'confirmPlain.result.turnOn': '今使っている人にも、すぐに反映されます。',
  'confirmPlain.result.settings': '今使っている人にも、すぐに反映されます。',
  'confirmPlain.result.settingsInvisible': '使っている人から見た変化はありません。',
  'confirmPlain.result.domainAdd': 'ドメインを買ったサービスで設定すると、開けるようになります。',
  'confirmPlain.result.domainRemove': 'このアドレスは、すぐに開けなくなります。',
  'confirmPlain.result.serverDomainRemove': 'このアドレスはすぐに開けなくなり、ほかの人が使えるようになります。',
  'confirmPlain.result.serverOn': '使った分だけ、チャージ残高から引かれます。',
  'confirmPlain.result.serverOff': 'もう一度動かすまで、サーバーを使う機能は動きません。',
  'confirmPlain.result.serverDelete': '保存したデータは消えません。',
  'confirmPlain.result.serverPurge': 'すべて消えます。元に戻せません。',
  'confirmPlain.testOnly': 'テスト用のアプリだけが対象です。公開中のアプリは変わりません。',
  // 公開の確認の「何が変わるか」（2026-10-25 security/prompt-lava-fixes の D）。「版」「本番」「プログラム」は使わない（PLAIN_FORBIDDEN）
  'confirmPlain.files.first': '初めての公開です。ファイルは {count} 個です。',
  'confirmPlain.files.same': 'いま公開中のものと同じファイルです。',
  'confirmPlain.files.changed': 'いま公開中のものと比べて、追加 {added}・変更 {changed}・削除 {removed} ファイル。',
  'confirmPlain.files.addedLabel': '追加:',
  'confirmPlain.files.changedLabel': '変更:',
  'confirmPlain.files.removedLabel': '削除:',
  'confirmPlain.files.more': 'ほか {count} 個',
  'confirmPlain.testLink': '押す前にテスト用のアプリで試せます: {url}',
  'confirmPlain.testSame': '中身はこれと同じです。',
  'confirmPlain.testDiffers': 'テスト用のアプリの中身は、これとは違います。テスト用で試しても、公開されるものは確かめられません。',
  'confirmPlain.hosts': 'サーバーの更新に、外のアドレスが書かれています: {list}。知らないアドレスがあれば「やめる」を押してください。',
  'confirmPlain.hostsMore': '、ほか {count} 個',
  'confirmPlain.result.serverDeleteInvalid': 'お願いの形が正しくないため、押しても何も起きません。',
  'confirmPlain.result.serverUpdate': '今使っている人にも、すぐに反映されます。',
  'confirmPlain.result.secretPut': 'パスワードやキーなどの大事な値です。この画面には表示しません。',
  'confirmPlain.result.secretDelete': 'この値を使っている機能は動かなくなります。',
  // データの書き換え（db_query）= SQL から（sqlEffect.ts）。{tables} は SQL に書かれたままの表の名前（最大 3 つ・tableJoin でつなぐ）
  'confirmPlain.result.sqlRead': 'データを読むだけで、何も変わりません。',
  'confirmPlain.result.sqlSchema': '「{tables}」の保存形式を変更します。保存済みのデータは残ります。',
  'confirmPlain.result.sqlSchemaAny': 'データの保存形式を変更します。保存済みのデータは残ります。',
  'confirmPlain.result.sqlInsert': '「{tables}」にデータを追加します。',
  'confirmPlain.result.sqlUpdate': '「{tables}」のデータを書き換えます。元に戻せません。',
  'confirmPlain.result.sqlDelete': '「{tables}」のデータを消します。元に戻せません。',
  'confirmPlain.result.sqlDropColumn': '「{tables}」の一部の項目を、中身ごと消します。元に戻せません。',
  'confirmPlain.result.sqlDrop': '「{tables}」を中身ごと消します。元に戻せません。',
  'confirmPlain.result.sqlUnknown': 'どのデータが変わるか、この画面では判断できません。わからなければ「やめる」を押して、AI に何をするのか聞いてください。',
  // データの保存形式の変更（db_migrate）
  'confirmPlain.result.dataShapeKeep': '保存済みのデータは残ります。',
  'confirmPlain.result.dataShapeDrop': '「{tables}」は中身ごと消えます。元に戻せません。',
  'confirmPlain.result.dataShapeDropColumn': '「{tables}」の一部の項目は、中身ごと消えます。元に戻せません。',
  'confirmPlain.result.ban': 'ログアウトされ、使えなくなります。',
  'confirmPlain.result.unban': 'また使えるようになります。',
  'confirmPlain.result.appealAccept': '利用停止が解除され、あなたの返事が届きます。',
  'confirmPlain.result.appealReject': '利用停止はそのままで、あなたの返事が届きます。',
  'confirmPlain.result.mailSchedule': '決めた日時に、利用者にメールが届きます。',
  'confirmPlain.result.mailRepeat': '決めた間隔で、利用者にメールが届きます。',
  'confirmPlain.result.mailSend': 'すぐに利用者に届きます。送ったあとは取り消せません。',
  'confirmPlain.result.other': '何が変わるか、この画面では判断できません。わからなければ「やめる」を押して、AI に何をするのか聞いてください。',
  'confirmPlain.result.andMore': 'ほかに {count} 件の変更もいっしょに行います。'
};
